import { useCallback, useEffect, useState } from "react";
import { Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Favorites — V1.1.1 OP-001 · Customer Productivity (Batch B item #1).
 *
 * Per-user pins persisted in `db.favorite_customers`. Exposed via two
 * primitives:
 *   • useFavorites()      — global-ish hook: cached Set of ids + toggle.
 *                          One fetch per session; toggles hit the API
 *                          then optimistically update the local set.
 *   • <FavoriteButton />  — star icon that reads & toggles via the hook.
 *
 * The store is a plain module-level Set so multiple mount points
 * (Customer 360 drawer + Customers page toggle + New Order picker) all
 * observe the same state without a Context Provider. Change → subscribers
 * re-render via a bumped version counter.
 */

let CACHE = null;             // Set<customer_id> once loaded
let LOADED = false;
let LOADING = null;           // in-flight promise (dedupes concurrent calls)
let LIST_CACHE = [];          // full list for widgets that render metadata
let VERSION = 0;
const SUBS = new Set();

function bump() {
  VERSION += 1;
  for (const cb of SUBS) cb();
}

async function ensureLoaded() {
  if (LOADED) return;
  if (!LOADING) {
    LOADING = api.get("/customers/favorites").then((r) => {
      LIST_CACHE = r.data || [];
      CACHE = new Set(LIST_CACHE.map((f) => f.customer_id));
      LOADED = true;
      LOADING = null;
      bump();
    }).catch((e) => {
      LOADING = null;
      throw e;
    });
  }
  return LOADING;
}

async function pin(customerId) {
  await api.post(`/customers/favorites/${encodeURIComponent(customerId)}`);
  await refreshList();
}

async function unpin(customerId) {
  await api.delete(`/customers/favorites/${encodeURIComponent(customerId)}`);
  await refreshList();
}

async function refreshList() {
  const r = await api.get("/customers/favorites");
  LIST_CACHE = r.data || [];
  CACHE = new Set(LIST_CACHE.map((f) => f.customer_id));
  bump();
}

export function useFavorites() {
  const [, setV] = useState(VERSION);
  useEffect(() => {
    const cb = () => setV(VERSION);
    SUBS.add(cb);
    ensureLoaded().catch(() => {});
    return () => { SUBS.delete(cb); };
  }, []);

  const isFavorite = useCallback((id) => !!CACHE && CACHE.has(id), []);

  const toggle = useCallback(async (customerId, customerName) => {
    if (!CACHE) await ensureLoaded();
    const wasFav = CACHE.has(customerId);
    try {
      if (wasFav) {
        await unpin(customerId);
        toast.success(`Removed ${customerName || "customer"} from favorites`);
      } else {
        await pin(customerId);
        toast.success(`Pinned ${customerName || "customer"} to favorites`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update favorite");
    }
  }, []);

  return {
    favorites: LIST_CACHE,
    loaded: LOADED,
    isFavorite,
    toggle,
    reload: refreshList,
  };
}

/**
 * FavoriteButton — small star icon that toggles a customer's favorite state
 * for the current user. Show anywhere a customer name is shown.
 */
export function FavoriteButton({ customerId, customerName, className = "", size = 16, testid }) {
  const { isFavorite, toggle } = useFavorites();
  const pinned = isFavorite(customerId);
  if (!customerId) return null;
  return (
    <button
      type="button"
      data-testid={testid || `favorite-toggle-${customerId}`}
      data-favorite={pinned ? "true" : "false"}
      onClick={(e) => { e.stopPropagation(); toggle(customerId, customerName); }}
      aria-pressed={pinned}
      aria-label={pinned ? "Remove favorite" : "Pin favorite"}
      title={pinned ? "Pinned favorite" : "Pin favorite"}
      className={`inline-flex items-center justify-center rounded-md p-1 transition hover:bg-surface-alt ${
        pinned ? "text-amber-500" : "text-slate-400 hover:text-amber-500"
      } ${className}`}
    >
      <Star size={size} weight={pinned ? "fill" : "regular"} />
    </button>
  );
}
